import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Button,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from '@/components/ui';
import {
  useAnswerAskUser,
  useCancelAskUser,
} from '@/hooks/useInbox';
import type {
  AskUserAnswer,
  AskUserRequest,
  InboxItem,
} from '@/types/inbox';
import {
  AskUserQuestionField,
  isAnswerComplete,
} from './AskUserQuestionField';
import { NoSpaceNotice } from './NoSpaceNotice';
import { RequestStatusBanner } from './RequestStatusBanner';

interface AskUserAnswerCardProps {
  item: InboxItem;
  request: AskUserRequest | undefined;
  isLoadingRequest?: boolean;
}

export function AskUserAnswerCard({
  item,
  request,
  isLoadingRequest,
}: AskUserAnswerCardProps) {
  const toast = useToast();
  const answer = useAnswerAskUser({ status: 'pending' });
  const cancel = useCancelAskUser({ status: 'pending' });

  const [answers, setAnswers] = useState<Record<string, AskUserAnswer>>({});
  const [activeTab, setActiveTab] = useState<string | null>(null);

  // Reset local state when the source request changes.
  useEffect(() => {
    setAnswers({});
    setActiveTab(null);
  }, [request?.askUserRequestId]);

  const canAnswer = !!item.spaceId;
  const isSubmitting = answer.isPending || cancel.isPending;

  if (isLoadingRequest && !request) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading question…
      </div>
    );
  }

  if (!request) {
    return (
      <p className="text-sm text-muted-foreground">
        Question details are unavailable.
      </p>
    );
  }

  // When the request flipped status from elsewhere (another tab, agent
  // recall, run cancel, TTL), the form is no longer actionable — swap to a
  // banner per the chosen UX. Any locally typed answers are discarded.
  if (request.status !== 'pending') {
    return <RequestStatusBanner item={item} request={request} />;
  }

  const askUserRequestId = request.askUserRequestId;
  const questions = request.questions ?? [];

  function setQuestionAnswer(next: AskUserAnswer) {
    setAnswers((prev) => ({ ...prev, [next.questionId]: next }));
  }

  const isQuestionComplete = (questionId: string) =>
    isAnswerComplete(answers[questionId]?.value);

  const allComplete = questions.every((q) => isQuestionComplete(q.questionId));

  const currentTab = activeTab ?? questions[0]?.questionId ?? '';

  const tabLabels = questions.map((q, idx) => ({
    id: q.questionId,
    label: q.header || `Q${idx + 1}`,
  }));

  async function submit() {
    if (!item.spaceId) return;
    const payload = questions.map((q) => {
      const a = answers[q.questionId];
      return {
        questionId: q.questionId,
        value: a?.value ?? '',
        ...(a?.comment ? { comment: a.comment } : {}),
      };
    });
    try {
      await answer.mutateAsync({
        spaceId: item.spaceId,
        requestId: askUserRequestId,
        inboxItemId: item.inboxItemId,
        answers: payload,
      });
      toast.success('Answer sent');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not send answer',
      );
    }
  }

  async function dismiss() {
    if (!item.spaceId) return;
    try {
      await cancel.mutateAsync({
        spaceId: item.spaceId,
        requestId: askUserRequestId,
        inboxItemId: item.inboxItemId,
        reason: 'user_dismissed',
      });
      toast.success('Request dismissed');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not dismiss');
    }
  }

  return (
    <div className="space-y-4">
      {questions.length === 1 ? (
        <AskUserQuestionField
          question={questions[0]}
          answer={answers[questions[0].questionId]}
          onChange={setQuestionAnswer}
          disabled={!canAnswer || isSubmitting}
        />
      ) : (
        <Tabs value={currentTab} onValueChange={setActiveTab}>
          <TabsList className="h-auto w-full justify-start rounded-none border-b bg-transparent p-0">
            {tabLabels.map(({ id, label }) => (
              <TabsTrigger
                key={id}
                value={id}
                className="relative rounded-none border-b-2 border-b-transparent bg-transparent px-4 pb-3 pt-2 font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-b-primary data-[state=active]:text-foreground data-[state=active]:shadow-none"
              >
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          {questions.map((q) => (
            <TabsContent key={q.questionId} value={q.questionId} className="mt-6">
              <AskUserQuestionField
                question={q}
                answer={answers[q.questionId]}
                onChange={setQuestionAnswer}
                disabled={!canAnswer || isSubmitting}
              />
            </TabsContent>
          ))}
        </Tabs>
      )}

      {!canAnswer && <NoSpaceNotice />}

      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          variant="ghost"
          onClick={dismiss}
          disabled={!canAnswer || isSubmitting}
        >
          Dismiss
        </Button>
        <Button
          onClick={submit}
          disabled={!canAnswer || !allComplete || isSubmitting}
        >
          {answer.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {item.actionLabel || 'Answer'}
        </Button>
      </div>
    </div>
  );
}
